import { Injectable, inject } from '@angular/core';
import { ValidationContextService } from './validation-context.service';
import { extractTaxonomyFromSchema } from './validation-taxonomy.util';
import { SHEET_COLUMN_TYPE_MAP, getColumnLabel, toComparisonPrecision, MBRS_EQUALITY_TOLERANCE, diffMeetsTolerance } from './validation-field-key.util';
import { getFieldValue, getFieldValueForColumn } from './validation-field-value.util';
import { buildMismatchMessage } from './equality-message';
import { isNumericControl } from './validation-control-type.util';
import { CrossSheetCalcDebugService } from './cross-sheet-calc-debug.service';
import { EqualityValidation } from '../../models/validation-rules.model';

/**
 * Equality validation error result
 */
export interface EqualityValidationError {
  /** Error severity (always ERROR for equality) */
  severity: 'ERROR' | 'WARNING';

  /** Error message to display */
  message: string;

  /** Validation type */
  type: 'equality';

  /** Reference to the field being compared */
  compareWith: {
    sheet: string;
    field: string;
  };

  /** Actual difference between values */
  difference: number;

  /** Tolerance used for comparison */
  tolerance: number;
}

/**
 * EqualityValidationService
 *
 * Owns schema-driven equality validation: comparing a field's value against a
 * referenced field (or sum of referenced fields), the same-sheet warning
 * propagation, and the equality-link map. Relocated verbatim from the legacy
 * combined validation-warning service as part of the validation-warning split; helper
 * calls are rewired to the pure utils and ValidationContextService.
 */
@Injectable({
  providedIn: 'root'
})
export class EqualityValidationService {
  private ctx = inject(ValidationContextService);
  private crossSheetCalcDebug = inject(CrossSheetCalcDebugService);

  /**
   * Validate all fields with equality validation rules
   *
   * @param schema - Form schema with all controls
   * @param formValues - Current form values (from getRawValue())
   * @param currentTaxonomy - Current taxonomy ("MFRS" or "MPERS")
   * @param currentSheetKey - Current sheet key (e.g., "statementOfProfitOrLoss") for accessing current sheet data
   * @returns Map of field qname to validation errors
   */
  validateAllEqualityRules(
    schema: any,
    formValues: any,
    currentTaxonomy: 'MFRS' | 'MPERS',
    currentSheetKey?: string
  ): { [qname: string]: EqualityValidationError[] } {
    const errors: { [qname: string]: EqualityValidationError[] } = {};

    if (!schema || !schema.controls) {
      return errors;
    }

    // Extract schema's taxonomy (FS-MFRS → MFRS, FS-MPERS → MPERS)
    const schemaTaxonomy = extractTaxonomyFromSchema(schema);

    // STRICT MODE: Skip all validations if schema taxonomy doesn't match current taxonomy
    if (!schemaTaxonomy) {
      // No taxonomy in schema - skip all validations (Option A: Safe)
      console.warn('⚠️ Schema missing taxonomy field - skipping all equality validations');
      return errors;
    }

    if (schemaTaxonomy !== currentTaxonomy) {
      // Schema taxonomy doesn't match current taxonomy - skip all validations
      return errors;
    }

    // Cache the schema for label lookups in generateEqualityMessage()
    if (currentSheetKey) {
      this.ctx.cacheSchema(currentSheetKey, schema);
    }

    // Determine if current sheet is columnar (bracket-suffixed keys)
    const isColumnar = !!(currentSheetKey && SHEET_COLUMN_TYPE_MAP[currentSheetKey]);

    // Iterate through all controls
    schema.controls.forEach((control: any) => {
      // Skip if no equality validation
      if (!control.validation?.equality) {
        return;
      }

      // Check if this is a sum validation (all rules have operation: 'sum')
      const isSumValidation = control.validation.equality.every((rule: EqualityValidation) => rule.operation === 'sum');

      if (isColumnar && currentSheetKey) {
        // COLUMN-AWARE VALIDATION: Iterate over each visible column suffix
        const columnSuffixes = this.ctx.getVisibleColumnSuffixes();

        if (isSumValidation) {
          // Column-aware SUM validation
          for (const suffix of columnSuffixes) {
            const columnLabel = getColumnLabel(suffix);
            const currentResolvedKey = this.ctx.resolveSheetKey(currentSheetKey) ?? currentSheetKey;
            const currentValue = getFieldValueForColumn(formValues, control.qname, currentResolvedKey, suffix, currentTaxonomy, this.ctx.getCachedSchema(currentResolvedKey));

            if (currentValue === null) continue;

            let sumValue = 0;
            const sourceFields: string[] = [];

            for (const rule of control.validation.equality) {
              if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
                continue;
              }

              const ruleResolvedKey = this.ctx.resolveSheetKey(rule.sheet) ?? rule.sheet;
              const sourceValue = getFieldValueForColumn(formValues, rule.field, ruleResolvedKey, suffix, currentTaxonomy, this.ctx.getCachedSchema(ruleResolvedKey));
              if (sourceValue === null) continue;

              sumValue += sourceValue;
              sourceFields.push(rule.field);
            }

            const tolerance = control.validation.equality[0]?.tolerance ?? MBRS_EQUALITY_TOLERANCE;
            const difference = Math.abs(toComparisonPrecision(currentValue) - toComparisonPrecision(sumValue));

            if (diffMeetsTolerance(difference, tolerance)) {
              // Use custom message from first rule if provided, otherwise generate default
              const customMessage = control.validation.equality[0]?.message;
              const message = customMessage
                ? `${columnLabel}: ${customMessage}`
                : `${columnLabel}: The value should equal the sum of: ${sourceFields.join(' + ')}. Expected: ${sumValue}, Actual: ${currentValue}`;

              if (!errors[control.qname]) {
                errors[control.qname] = [];
              }

              errors[control.qname].push({
                severity: 'ERROR',
                message: message,
                type: 'equality',
                compareWith: {
                  sheet: control.validation.equality[0].sheet,
                  field: sourceFields.join(' + ')
                },
                difference: difference,
                tolerance: tolerance
              });
            }
          }
        } else {
          // Column-aware STANDARD EQUALITY validation
          control.validation.equality.forEach((rule: EqualityValidation) => {
            if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
              return;
            }

            for (const suffix of columnSuffixes) {
              const columnLabel = getColumnLabel(suffix);

              const currentResolvedKey = this.ctx.resolveSheetKey(currentSheetKey) ?? currentSheetKey;
              const ruleResolvedKey = this.ctx.resolveSheetKey(rule.sheet) ?? rule.sheet;
              const rawCurrentValue = getFieldValueForColumn(formValues, control.qname, currentResolvedKey, suffix, currentTaxonomy, this.ctx.getCachedSchema(currentResolvedKey));
              const rawCompareValue = getFieldValueForColumn(formValues, rule.field, ruleResolvedKey, suffix, currentTaxonomy, this.ctx.getCachedSchema(ruleResolvedKey));

              // Skip if both are null (requirement: when both is null, skip the validation)
              if (rawCurrentValue === null && rawCompareValue === null) {
                continue;
              }

              // Conditional validation flags
              if (rule.skipIfSourceNull && rawCurrentValue === null) continue;
              if (rule.skipIfTargetNull && rawCompareValue === null) continue;

              // Treat an empty cell as 0 ONLY for numeric/monetary controls — a
              // null numeric field effectively displays as 0, so coercing it
              // surfaces a cross-sheet mismatch even when one side is unfilled.
              // Text/date controls must NOT be coerced (an empty date is not "0");
              // their un-coerced null falls through to the typeof guard below.
              const compareControl = this.ctx.getCachedSchema(ruleResolvedKey)
                ?.controls?.find((control: any) => control.qname === rule.field);
              const currentValue = rawCurrentValue ?? (isNumericControl(control) ? 0 : null);
              const compareValue = rawCompareValue ?? (isNumericControl(compareControl) ? 0 : null);

              if (typeof currentValue !== 'number' || typeof compareValue !== 'number') {
                continue;
              }

              const tolerance = rule.tolerance ?? MBRS_EQUALITY_TOLERANCE;
              const difference = Math.abs(toComparisonPrecision(currentValue) - toComparisonPrecision(compareValue));

              // Diagnostic only (no-op unless RuntimeConfig.crossSheetCalcDebug).
              // This path DOES round to 2 d.p.; the compare value is still read
              // from the persisted snapshot, so a flag here points at a
              // stale/un-recomputed compared value rather than rounding.
              this.crossSheetCalcDebug.log({
                source: 'equality-validation:columnar',
                currentSheet: currentResolvedKey,
                currentField: control.qname,
                compareSheet: ruleResolvedKey,
                compareField: rule.field,
                column: columnLabel,
                currentRaw: rawCurrentValue,
                compareRaw: rawCompareValue,
                tolerance,
                rounds: true,
              });

              if (diffMeetsTolerance(difference, tolerance)) {
                const baseMessage = rule.message ?? this.generateEqualityMessage(
                  rule.field,
                  rule.sheet,
                  currentTaxonomy
                );
                const message = `${columnLabel}: ${baseMessage}`;

                if (!errors[control.qname]) {
                  errors[control.qname] = [];
                }

                errors[control.qname].push({
                  severity: 'ERROR',
                  message: message,
                  type: 'equality',
                  compareWith: {
                    sheet: rule.sheet,
                    field: rule.field
                  },
                  difference: difference,
                  tolerance: tolerance
                });
              }
            }
          });
        }
      } else {
        // NON-COLUMNAR FALLBACK: Original flat-lookup logic
        if (isSumValidation) {
          const currentResolvedKey = this.ctx.resolveSheetKey(currentSheetKey);
          const currentValue = getFieldValue(formValues, control.qname, currentResolvedKey, this.ctx.getCachedSchema(currentResolvedKey ?? ''));
          if (currentValue === null || currentValue === undefined) return;

          let sumValue = 0;
          const sourceFields: string[] = [];

          for (const rule of control.validation.equality) {
            if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
              continue;
            }
            const ruleResolvedKey = this.ctx.resolveSheetKey(rule.sheet);
            const sourceValue = getFieldValue(formValues, rule.field, ruleResolvedKey, this.ctx.getCachedSchema(ruleResolvedKey ?? ''));
            if (sourceValue === null || sourceValue === undefined) continue;
            sumValue += sourceValue;
            sourceFields.push(rule.field);
          }

          const tolerance = control.validation.equality[0]?.tolerance ?? MBRS_EQUALITY_TOLERANCE;
          const difference = Math.abs(toComparisonPrecision(currentValue) - toComparisonPrecision(sumValue));

          if (diffMeetsTolerance(difference, tolerance)) {
            const message = `The value should equal the sum of: ${sourceFields.join(' + ')}. Expected: ${sumValue}, Actual: ${currentValue}`;
            if (!errors[control.qname]) {
              errors[control.qname] = [];
            }
            errors[control.qname].push({
              severity: 'ERROR',
              message: message,
              type: 'equality',
              compareWith: {
                sheet: control.validation.equality[0].sheet,
                field: sourceFields.join(' + ')
              },
              difference: difference,
              tolerance: tolerance
            });
          }
        } else {
          control.validation.equality.forEach((rule: EqualityValidation) => {
            if (rule.taxonomy && rule.taxonomy.toUpperCase() !== currentTaxonomy.toUpperCase()) {
              return;
            }

            const currentResolvedKey = this.ctx.resolveSheetKey(currentSheetKey);
            const ruleResolvedKey = this.ctx.resolveSheetKey(rule.sheet);
            const rawCurrentValue = getFieldValue(formValues, control.qname, currentResolvedKey, this.ctx.getCachedSchema(currentResolvedKey ?? ''));
            const rawCompareValue = getFieldValue(formValues, rule.field, ruleResolvedKey, this.ctx.getCachedSchema(ruleResolvedKey ?? ''));

            // Skip if both are null/undefined
            if ((rawCurrentValue === null || rawCurrentValue === undefined) &&
                (rawCompareValue === null || rawCompareValue === undefined)) {
              return;
            }

            if (rule.skipIfSourceNull && (rawCurrentValue === null || rawCurrentValue === undefined)) return;
            if (rule.skipIfTargetNull && (rawCompareValue === null || rawCompareValue === undefined)) return;

            // Treat an empty cell as 0 ONLY for numeric/monetary controls (see
            // columnar branch above for rationale). Text/date controls keep
            // their un-coerced null and are skipped by the typeof guard below.
            const compareControl = this.ctx.getCachedSchema(ruleResolvedKey ?? '')
              ?.controls?.find((control: any) => control.qname === rule.field);
            const currentValue = rawCurrentValue ?? (isNumericControl(control) ? 0 : null);
            const compareValue = rawCompareValue ?? (isNumericControl(compareControl) ? 0 : null);

            if (typeof currentValue !== 'number' || typeof compareValue !== 'number') return;

            const tolerance = rule.tolerance ?? MBRS_EQUALITY_TOLERANCE;
            const difference = Math.abs(toComparisonPrecision(currentValue) - toComparisonPrecision(compareValue));

            if (diffMeetsTolerance(difference, tolerance)) {
              const message = rule.message ?? this.generateEqualityMessage(
                rule.field,
                rule.sheet,
                currentTaxonomy
              );

              if (!errors[control.qname]) {
                errors[control.qname] = [];
              }

              errors[control.qname].push({
                severity: 'ERROR',
                message: message,
                type: 'equality',
                compareWith: {
                  sheet: rule.sheet,
                  field: rule.field
                },
                difference: difference,
                tolerance: tolerance
              });
            }
          });
        }
      }
    });

    return errors;
  }

  /**
   * Generate equality error message
   * Format: "Mismatch in value provided for the same field. The value should match [Field Name] in [Sheet Name]."
   *
   * @param compareFieldQname - Field qname to compare against
   * @param compareSheetKey - Sheet key containing the compare field
   * @param taxonomy - Current taxonomy for label lookup
   * @returns Error message
   */
  private generateEqualityMessage(
    compareFieldQname: string,
    compareSheetKey: string,
    taxonomy: 'MFRS' | 'MPERS'
  ): string {
    const fieldLabel = this.ctx.resolveFieldLabel(compareFieldQname, compareSheetKey, taxonomy);

    // Always use the sheet-label fallback for sheet name to match the sheet dropdown labels
    const sheetLabel = this.ctx.sheetLabel(compareSheetKey);

    return buildMismatchMessage({ fieldLabel, sheetLabel });
  }

  /**
   * Build a field-mismatch message from qnames/sheet keys — the single entry
   * point sheets call instead of hand-assembling the string. Resolves the field
   * label via the shared schema cache (preloadReferencedSchemas must have run,
   * or the field's own sheet must already be cached), then delegates wording to
   * the pure buildMismatchMessage().
   *
   * @param args.columnLabel   Column prefix, e.g. "Separate Current" (omit for the bare core).
   * @param args.fieldQname    Reference field qname (or an already-human label, which passes through).
   * @param args.fieldSheetKey State-service key of the sheet holding fieldQname (for label + sheet-name resolution).
   * @param args.taxonomy      Current taxonomy for FormSchemaService lookup.
   * @param args.qualifier     Optional nested prefix segment (e.g. an equity column label).
   * @param args.section       Optional "(under section …)" qualifier.
   * @param args.sheetLabelOverride  Explicit sheet display name; defaults to ctx.sheetLabel(fieldSheetKey).
   */
  formatFieldMismatch(args: {
    columnLabel?: string;
    fieldQname: string;
    fieldSheetKey: string;
    taxonomy: 'MFRS' | 'MPERS';
    qualifier?: string;
    section?: string;
    sheetLabelOverride?: string;
  }): string {
    const fieldLabel = this.ctx.resolveFieldLabel(args.fieldQname, args.fieldSheetKey, args.taxonomy);
    const sheetLabel = args.sheetLabelOverride ?? this.ctx.sheetLabel(args.fieldSheetKey);
    return buildMismatchMessage({
      columnLabel: args.columnLabel,
      qualifier: args.qualifier,
      fieldLabel,
      section: args.section,
      sheetLabel,
    });
  }

  /**
   * Propagate API mismatch warnings between fields linked by same-sheet equality validation
   *
   * This function reads the schema's equality validation rules to find fields that are
   * linked to each other within the same sheet (e.g., ProfitLoss ↔ ProfitLoss_2).
   * When one field has a mismatch warning (from API data comparison), the same warning
   * should be displayed on the linked field since they share the same underlying data.
   *
   * @param schema - Form schema containing controls with validation rules
   * @param validationErrors - Map of qname to error messages (will be mutated)
   * @param currentSheetKey - The current sheet key (e.g., 'statementOfProfitOrLoss')
   * @param mismatchPattern - Pattern to identify mismatch warnings (default: 'does not match the extracted sum')
   */
  propagateSameSheetMismatchWarnings(
    schema: any,
    validationErrors: Map<string, string[]>,
    currentSheetKey: string,
    mismatchPattern: string = 'does not match the extracted sum'
  ): void {
    if (!schema || !schema.controls) {
      return;
    }

    // Build a map of same-sheet equality relationships: qname -> linked qnames
    const sameSheetEqualityLinks = new Map<string, string[]>();

    schema.controls.forEach((control: any) => {
      if (!control.validation?.equality) {
        return;
      }

      // Find equality rules that point to fields in the same sheet
      const sameSheetLinks = control.validation.equality
        .filter((rule: any) => rule.sheet === currentSheetKey)
        .map((rule: any) => rule.field);

      if (sameSheetLinks.length > 0) {
        sameSheetEqualityLinks.set(control.qname, sameSheetLinks);
      }
    });

    // Propagate mismatch warnings between linked fields
    sameSheetEqualityLinks.forEach((linkedFields, sourceQname) => {
      const sourceErrors = validationErrors.get(sourceQname);

      if (sourceErrors && sourceErrors.length > 0) {
        // Filter for mismatch warnings only
        const mismatchWarnings = sourceErrors.filter((msg) =>
          msg.includes(mismatchPattern)
        );

        if (mismatchWarnings.length > 0) {
          // Propagate to all linked fields
          linkedFields.forEach((linkedQname) => {
            const existingErrors = validationErrors.get(linkedQname) || [];

            // Avoid duplicating warnings that already exist
            const newWarnings = mismatchWarnings.filter(
              (warning) => !existingErrors.includes(warning)
            );

            if (newWarnings.length > 0) {
              validationErrors.set(linkedQname, [...existingErrors, ...newWarnings]);
            }
          });
        }
      }
    });
  }

  /**
   * Get same-sheet equality linked fields from schema
   * Useful for components to know which fields share the same API data
   *
   * @param schema - Form schema containing controls with validation rules
   * @param currentSheetKey - The current sheet key
   * @returns Map of qname to array of linked qnames in the same sheet
   */
  getSameSheetEqualityLinks(
    schema: any,
    currentSheetKey: string
  ): Map<string, string[]> {
    const links = new Map<string, string[]>();

    if (!schema || !schema.controls) {
      return links;
    }

    schema.controls.forEach((control: any) => {
      if (!control.validation?.equality) {
        return;
      }

      const sameSheetLinks = control.validation.equality
        .filter((rule: any) => rule.sheet === currentSheetKey)
        .map((rule: any) => rule.field);

      if (sameSheetLinks.length > 0) {
        links.set(control.qname, sameSheetLinks);
      }
    });

    return links;
  }
}
